import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

/**
 * Auto-logout for inactivity.
 *
 * Fetches `auto_logout_minutes` from `/api/store-settings` and starts a
 * single idle timer that fires `logout()` after that many minutes of zero
 * user input.  A toast warns the user 30 seconds before the boot.
 *
 * • Activity events: pointer, key, scroll, touch (broadcast across tabs via
 *   localStorage so multi-window usage doesn't accidentally log a user out
 *   while they're using a different window).
 * • `auto_logout_minutes <= 0` or unset → feature disabled (no timer).
 * • Logout is *just* logout — it does NOT clock the user out (clock-out is
 *   a separate explicit action on the Clock screen).
 */
const IDLE_EVENTS = ["mousedown", "mousemove", "keydown", "touchstart", "scroll", "wheel"];
const STORAGE_KEY = "pos_last_activity";
const WARN_SECONDS = 30;

export const useAutoLogout = () => {
  const { logout, isAuthenticated, user } = useAuth();
  const [minutes, setMinutes] = useState(0);
  const timerRef = useRef(null);
  const warnRef = useRef(null);
  const warnedRef = useRef(false);

  // Fetch the configured timeout once the user is authenticated.
  useEffect(() => {
    if (!isAuthenticated) return;
    let ignore = false;
    axios.get(`${API}/store-settings`)
      .then((res) => {
        if (ignore) return;
        const m = Number(res.data?.auto_logout_minutes) || 0;
        setMinutes(m);
      })
      .catch(() => { /* non-fatal — leave feature disabled */ });
    return () => { ignore = true; };
  }, [isAuthenticated]);

  // Drive the idle timer.
  useEffect(() => {
    if (!isAuthenticated || !minutes || minutes <= 0) return;
    // Clock-only kiosks shouldn't auto-logout — that screen IS the login.
    if (user?.role === "kds") return;

    const timeoutMs = minutes * 60 * 1000;
    const warnMs = Math.max(0, timeoutMs - WARN_SECONDS * 1000);

    const resetTimer = () => {
      warnedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);

      warnRef.current = setTimeout(() => {
        if (!warnedRef.current) {
          warnedRef.current = true;
          toast.warning(`Auto-logout in ${WARN_SECONDS}s — move the mouse to stay signed in.`, {
            duration: WARN_SECONDS * 1000,
          });
        }
      }, warnMs);

      timerRef.current = setTimeout(() => {
        toast.info("Signed out due to inactivity");
        logout();
      }, timeoutMs);
    };

    const markActivity = () => {
      // Cross-tab broadcast — bumps every open POS window simultaneously.
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
      resetTimer();
    };

    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) resetTimer();
    };

    IDLE_EVENTS.forEach((evt) =>
      window.addEventListener(evt, markActivity, { passive: true })
    );
    window.addEventListener("storage", onStorage);
    resetTimer();

    return () => {
      IDLE_EVENTS.forEach((evt) => window.removeEventListener(evt, markActivity));
      window.removeEventListener("storage", onStorage);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
    };
  }, [isAuthenticated, minutes, user?.role, logout]);
};

/** Mount-only wrapper so the hook can be dropped into the protected tree. */
const IdleWatcher = () => {
  useAutoLogout();
  return null;
};

export default IdleWatcher;
